import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import type {
    AiTransformationItemsResponse,
    AiTransformationStatus,
    AiTransformationSubject,
} from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';
import { unwrapResult } from '../../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { POSITION_DERIVED_QUERY_META } from '../../../position-management';
import { useLocale } from '../../components/use-locale';
import { keepPreviousDataOfSubject, strategyKeys } from './query-keys';

const COLUMN_PAGE_SIZE = 30;

export const nextItemsPage = ({ page, size, total }: AiTransformationItemsResponse): number | undefined =>
    (page + 1) * size < total ? page + 1 : undefined;

/** One board column: the items in one status, a page at a time. */
export const useStrategyStatusColumn = ({
    architectureId,
    filters,
    status,
    subject,
}: {
    readonly architectureId: string;
    readonly filters: AiTransformationFilters;
    readonly status: AiTransformationStatus;
    readonly subject: AiTransformationSubject;
}): UseInfiniteQueryResult<InfiniteData<AiTransformationItemsResponse, number>, Error> => {
    const { fetchAiTransformationItems } = useApplicationFetchers();
    const acceptLanguage = useLocale();

    return useInfiniteQuery({
        getNextPageParam: nextItemsPage,
        initialPageParam: 0,
        placeholderData: keepPreviousDataOfSubject(subject),
        queryFn: ({ pageParam, signal }) =>
            unwrapResult(
                fetchAiTransformationItems({
                    acceptLanguage,
                    architectureId,
                    filters,
                    page: pageParam,
                    signal,
                    size: COLUMN_PAGE_SIZE,
                    statuses: [status],
                    subject,
                }),
            ),
        meta: POSITION_DERIVED_QUERY_META,
        queryKey: strategyKeys.column(architectureId, { filters, status, subject }, acceptLanguage),
        throwOnError: false,
    });
};
